import React from 'react';

import { ScreenWithOptionalParams } from './ScreenWithOptionalParams.type';

interface ContextValue
  extends Readonly<{
    current: ScreenWithOptionalParams;
    history: ScreenWithOptionalParams[];
    canPop: boolean;
    push: (screen: ScreenWithOptionalParams) => void;
    pop: () => void;
    reset: () => void;
  }> {}

const Context = React.createContext<ContextValue>({
  current: { screen: '' },
  history: [{ screen: '' }],
  canPop: false,
  push: (_screen: ScreenWithOptionalParams) => {
    throw new Error('StackNavigationContextProvider not found!');
  },
  pop: () => {
    throw new Error('StackNavigationContextProvider not found!');
  },
  reset: () => {
    throw new Error('StackNavigationContextProvider not found!');
  },
});

interface StackNavigationContextProviderProps
  extends Readonly<{
    initialScreen: ScreenWithOptionalParams;
    children?: React.ReactNode;
  }> {}

export const StackNavigationContextProvider = React.memo<StackNavigationContextProviderProps>((props) => {
  const { initialScreen, children } = props;

  const [history, setHistory] = React.useState<ScreenWithOptionalParams[]>(() => [initialScreen]);

  const Current = React.useMemo(() => history[history.length - 1], [history]);

  const CanPop = React.useMemo(() => history.length > 1, [history]);

  const PushCallback = React.useCallback(
    (screen: ScreenWithOptionalParams) => setHistory((prevHistory: ScreenWithOptionalParams[]) => [...prevHistory, screen]),
    []
  );

  const PopCallback = React.useCallback(() => {
    if (!CanPop) {
      return;
    }

    setHistory((prevHistory: ScreenWithOptionalParams[]) => {
      const updatedHistory = [...prevHistory];
      updatedHistory.pop();

      return updatedHistory;
    });
  }, [CanPop]);

  const ResetCallback = React.useCallback(() => setHistory([initialScreen]), [initialScreen]);

  const ContextValue = React.useMemo(() => {
    const contextValue: ContextValue = {
      history: [...history],
      current: Current,
      canPop: CanPop,
      push: PushCallback,
      pop: PopCallback,
      reset: ResetCallback,
    };

    return contextValue;
  }, [history, Current, CanPop, PushCallback, PopCallback, ResetCallback]);

  return <Context.Provider value={ContextValue}>{children}</Context.Provider>;
});

export const useStackNavigationContext = () => React.useContext<ContextValue>(Context);
